// =============================================================================
// Virtual coordinate systems
// =============================================================================
//
// Two virtual coord spaces run the show. Both are rendered at display scale
// via FitContainer (transform: scale(S)), so components write their math in
// virtual px and the layer above handles fitting:
//
//   Canvas virtual    = CANVAS_WIDTH × CANVAS_HEIGHT
//     The drawing buffer, the reference photo's layout box, and the
//     marcTransform's tx/ty all live in these px. This is THE shared
//     coord space — the whole point of keeping them equal is so an artist's
//     "feature X is at (400, 300)" mental model maps 1:1 between the
//     drawing and the reference.
//
//   Frame virtual     = FRAME_OUTER_WIDTH × FRAME_OUTER_HEIGHT
//     Used only by the drawing side's FitContainer so the gold frame PNG
//     and the canvas-plus-borders scale as one unit. Inside that, a nested
//     <Frame> re-establishes canvas virtual at offset (FRAME_SLICE_LEFT,
//     FRAME_SLICE_TOP). Its children (DrawCanvas, OnionMarc) work in
//     canvas virtual — which is why marcTransform behaves identically on
//     the drawing's OnionMarc and the reference's photo.
//
// Forward + inverse transforms for marcTransform live in src/virtualCoords.js.
// =============================================================================

// Canvas dims + validation caps live in shared/limits.js so the worker
// validator and the authoring UI can't drift apart. Re-exported here so
// existing src/ imports stay pointed at constants.
import {
  CANVAS_HEIGHT,
  CANVAS_WIDTH,
  MAX_EGG_LIGHTS,
} from "../shared/limits.js";

export { CANVAS_HEIGHT, CANVAS_WIDTH, MAX_EGG_LIGHTS };

export const DEFAULT_MARC_ID = "001";

// How many marcs make up a speedrun round. SpeedrunEngine shuffles the
// committed manifest and takes this many. ?marcs= overrides the random
// pick entirely.
export const MARCS_PER_ROUND = 3;

// Gold frame around the drawing canvas. The PNG at /frame.png is 994×1221
// with a 790×1018 transparent interior (matching CANVAS_WIDTH/HEIGHT).
// These offsets place the drawing surface inside that interior, assuming
// the opening is centered in the outer — tweak if it's off-center:
//   horizontal borders: (994 - 790) / 2 = 102 each side
//   vertical borders:   (1221 - 1018) = 203 total → 101 top / 102 bottom
export const FRAME_SLICE_TOP = 101;
export const FRAME_SLICE_RIGHT = 102;
export const FRAME_SLICE_BOTTOM = 102;
export const FRAME_SLICE_LEFT = 102;
export const FRAME_OUTER_WIDTH =
  CANVAS_WIDTH + FRAME_SLICE_LEFT + FRAME_SLICE_RIGHT;
export const FRAME_OUTER_HEIGHT =
  CANVAS_HEIGHT + FRAME_SLICE_TOP + FRAME_SLICE_BOTTOM;

// The canvas-bg (orange) extends this many virtual px past each edge of
// the drawing surface, tucked under the gold frame's inner lip so no
// sliver of the page bg leaks through at the frame opening — covers
// sub-pixel alpha at the PNG cutout edge and off-by-one slice offsets.
export const FRAME_BG_BLEED = 3;

// Six-slot palette. First four are "fixed-ish" — they apply to most photos
// of Marc and are worth tuning globally. Last two are the flex slots: one
// typically for shirts/collars/teeth (often white), the other for whatever
// Marc's wearing that day (navy sweater, gray suit, etc). All six can be
// overridden per-photo via eyedropper (right-click) or manual edit
// (double-click on a swatch).
export const DEFAULT_PALETTE = [
  "#eec3a1", // 1 · skin
  "#643e2c", // 2 · beard / hair
  "#e0988a", // 3 · lip
  "#111111", // 4 · black (eyes, dark line work)
  "#ffffff", // 5 · flex · often-white (shirt, collar, teeth)
  "#2d5fa0", // 6 · flex · variable (sweater, suit, jacket — override per photo)
];

export const ONION_OPACITY = 0.4;

export const DEFAULT_MARC_TRANSFORM = {
  tx: 0,
  ty: 0,
  scale: 1,
  rot: 0,
};

// Horizontal squish applied to the rendered egg to better suggest a head
// shape. 1.0 = natural width; <1 = thinner, >1 = wider. Per-marc value is
// tunable in author mode and saved into reference.json; this is the default
// for new marcs.
export const DEFAULT_EGG_SCALE_X = 0.92;

// All egg-positioning state in one bag — mirrors marcTransform's shape so
// the two surfaces feel symmetric. tx/ty are in canvas-virtual px and
// translate the egg in screen-axis (applied after rotate, so dragging
// right always moves right regardless of rotation).
export const DEFAULT_EGG_TRANSFORM = {
  tx: 0,
  ty: 0,
  scale: 1,
  scaleX: DEFAULT_EGG_SCALE_X,
  rotation: 0,
};

export const DEFAULT_EGG_ID = "egg1";

// Warmth tint applied to the egg photo (0-100). Tuned by eye against the
// current egg + a handful of marcs; the egg reads cold/blue at 0 and roughly
// matches Marc's skin tone at 30. Author-mode slider in `AuthorUIPanel` can
// override this for the session — it doesn't persist, so refreshing the page
// resets back to this default.
export const DEFAULT_EGG_WARMTH = 30;

// Redshift tint applied alongside warmth (0-100). Warmth biases the egg
// toward yellow/brown; redshift biases the same tint stack toward red, for
// marcs whose face reads visibly flushed/red. Default 30 — most marcs read
// a touch too yellow at warmth alone, so a small redshift baseline makes
// the egg feel like a face rather than parchment.
export const DEFAULT_EGG_REDSHIFT = 30;

// Egg relighting (per-marc, persisted in author localStorage).
//
//   direction  3-vector. xy in [-1, 1] (drag pad), z derived as
//              sqrt(1 - xy²) so the disk edge means a pure side light.
//              Default y=-0.6 puts the light near the top of the pad
//              (typical above-camera lighting); x=0 is centered.
//   intensity  0..1. Brightening magnitude. 0.20 is a gentle highlight
//              that reads as lit without overpowering the egg's natural
//              tone — most marcs need a tweak from there but rarely
//              starting from zero.
//   shadow     0..1. Darkening magnitude on the side facing away from
//              the light, applied independently of intensity.
//   size       0..1. Highlight broadness. 1 = Lambertian (broad gradient
//              across the lit half); 0 = tight specular spot. 0.30 is
//              a moderately tight highlight that suits portrait
//              relighting better than the broad Lambertian default.
export const DEFAULT_EGG_LIGHT = {
  direction: { x: 0, y: -0.6, z: Math.sqrt(1 - 0.6 * 0.6) },
  intensity: 0.2,
  shadow: 0.2,
  size: 0.3,
};

// Multi-light: lighting state is an array of independent lights. New
// marcs start empty so the save-time guard catches "I forgot to light
// it" before exporting (vs. the prior default of one zero-intensity
// light, which counted as "lit" but rendered flat). The author clicks
// "Add Light" in the popover to introduce the first one — which uses
// DEFAULT_EGG_LIGHT, the sensible starting point above.
export const DEFAULT_EGG_LIGHTS = [];

// Default brush sizes, expressed as fractions of CANVAS_WIDTH so they
// stay roughly the same proportional thickness if the canvas resolution
// changes. Stored in virtual canvas px (the FitContainer scales them to
// display px at render time).
export const DEFAULT_PEN_SIZE = Math.round(CANVAS_WIDTH * 0.02);
export const DEFAULT_ERASER_SIZE = Math.round(CANVAS_WIDTH * 0.03);

// FitContainer's default upscale cap. Infinity = let the frame grow as
// large as the responsive layout's max-width / max-height allows. Callers
// can still pass a stricter `maxScale` (used by the reference photo's
// "study" thumbnail to stay smaller than the gold-framed drawing).
export const MAX_FIT_SCALE = Infinity;

// Layout breakpoints. App.js's grid is the source of truth for what
// changes at each one.
export const TABLET_BREAKPOINT = 1100;
export const MOBILE_BREAKPOINT = 800;
export const COMPACT_PALETTE_BREAKPOINT = 480;

export const REFERENCE_VERSION = 1;
export const MIN_REGION_PIXELS = 8;
export const QUANTIZE_ALPHA_THRESHOLD = 128;

// Scoring defaults. Tuned for "find something that looks like this region"
// rather than strict pixel match. All of these are per-region overridable
// via fields on the region in reference.json.
//
// Search radius scales inversely with region size: tiny features (eyes,
// eyebrows) get very forgiving search boxes because they have no strong
// positional anchor on a bare egg; big features (sweater, beard) get
// modest ones because they already have obvious placement cues.
//
// The curve is piecewise linear in pixelCount:
//   pixelCount ≤ SMALL_REGION_PIXELS → MAX_SEARCH_RADIUS_PX
//   pixelCount ≥ LARGE_REGION_PIXELS → MIN_SEARCH_RADIUS_PX
//   between → linear interp
//
// Per-region `searchRadius` in reference.json overrides the curve entirely.
// `matchAnyColor: true` makes any ink count (same effect as adding the
// label to COLOR_AGNOSTIC_LABELS below).
export const MIN_SEARCH_RADIUS_PX = 23;
export const MAX_SEARCH_RADIUS_PX = 100;
export const SMALL_REGION_PIXELS = 400;
export const LARGE_REGION_PIXELS = 50000;

// You pass a region if ink assigned to it is at least this fraction of the
// region's original pixel count. Mass fraction scales with region size to
// mirror the search-radius curve: small features get a lenient fraction,
// large features a strict one, linear interp between SMALL_REGION_PIXELS and
// LARGE_REGION_PIXELS. Rationale — "draw a little more" means 100%+ area
// change for a pupil but 5-10% for a beard, so a flat fraction punishes the
// small stuff disproportionately. Per-region `minMassFraction` still wins.
export const DEFAULT_LARGE_MASS_FRACTION = 0.67;
export const DEFAULT_SMALL_MASS_FRACTION = 0.33;

// Canonical label vocabulary, ordered foundational → detail. Used for
// two things:
//   1. Speedrun target order. `scoreDrawing` sorts groups by canonical
//      rank first, then by pixel count for unlisted labels. So the
//      player draws clothes before face, lips before mouth (lips form
//      the slot the dark mouth fills), eyebrows before pupils, etc.
//   2. LabelApp suggestion chips. Drives consistent labels across
//      marcs — a typo or "lip" vs "lips" silently sorts to the back of
//      the list, which makes the divergence visible at scoring time.
//
// Order rationale: large/foundational regions are easier to anchor on a
// blank egg (give the player a backdrop early), and downstream features
// read more naturally once the underlying shape is in place. lips
// before mouth is deliberate — drawing the dark mouth slit only makes
// sense once the lip shape exists. teeth after mouth (they go inside).
//
// Add new labels here in the right slot rather than at the end —
// trailing labels score after everything canonical, which usually isn't
// what's wanted for a real feature.
export const CANONICAL_LABELS = [
  "background",
  "jacket",
  "sweater",
  "shirt",
  "tie",
  "neck",
  "hair",
  "beard",
  "nose",
  "lips",
  "mouth",
  "teeth",
  "eyes",
  "eyebrows",
  "pupils",
  "glasses",
];

// Labels whose subregions must each individually clear their own threshold
// (all-of mode) — drawing only one eye does not satisfy "eyes". Every other
// plural label defaults to any-of: pool hits across subregions and clear
// when the player has covered roughly one subregion's worth (so a single
// stroke spanning both halves of a split sweater also clears, since hits
// from both halves accumulate). Per-marc mode can be overridden in
// reference.json's `labelGroups` dict written by LabelApp.
export const DEFAULT_ALL_OF_LABELS = ["eyes", "eyebrows", "pupils"];

// Subregions above this pixelCount auto-split into halves along the
// longer axis at scoring time, with each half having its own threshold
// and contribution cap. Forces "spread" — drawing only the left half
// of a single-region sweater can't clear the bar even if it satisfies
// the mass fraction. Below this threshold, the region is treated as
// one piece (no spread requirement) — the size at which spatial
// distribution actually matters.
//
// Per-half pixel counts come from `region.halfPixels` written by
// saveReference (so an asymmetric mask credits each half against its
// actual reference content). References saved before this field was
// added fall back to a 50/50 estimate, which slightly under-credits
// asymmetric masks but doesn't break detection — re-save those
// references for accurate behavior.
export const SPREAD_REGION_PIXELS = 50000;

// Per-label difficulty downrank. The label's region is graded as
// though it had `sizeFactor × pixelCount` pixels — both the mass
// fraction curve and the search radius curve consult the smaller
// effective size. So a 0.4 factor on neck (25K actual) makes it
// score like a 10K region: lower mass-fraction requirement AND
// larger search radius. Both axes get more lenient simultaneously,
// which is exactly what neck needs (alignment slack between the
// egg's chin and the marc's chin is bidirectional).
//
// Add labels here when their structural difficulty doesn't match
// their pixel size. Keep the list short — the curve handles
// most labels correctly, and this is the escape hatch for
// outliers.
export const LABEL_DEFAULTS = {
  neck: { sizeFactor: 0.6 },
};

// Assignment tiebreaker: when a player component overlaps multiple eligible
// reference regions, prefer the one whose center is closest to the
// component's center. The assignment score is
//   overlap / (1 + distance_to_region_center / ASSIGNMENT_DISTANCE_SOFTENER_PX)
// so overlap still matters but far-away matches are penalized. Smaller
// softener = harsher distance penalty (more strict locality).
export const ASSIGNMENT_DISTANCE_SOFTENER_PX = 120;

// Labels where any ink color counts. Empty by default — most features have
// a canonical color traced in author mode and that color should be what
// scores them. Left here as a hatch for future features (e.g. "hair" if we
// decide any dark color should count).
export const COLOR_AGNOSTIC_LABELS = [];

// Overdraw cap: a region is "spilled" when the total size of player
// components attributed to it exceeds this multiple of the region's
// pixel count. Targets the accidental-flood-fill case — a fill that
// escapes its boundary covers way more than the region's mass and
// would otherwise instantly clear detection. When tripped, the
// region's score declines past full instead of holding at 1.0, and
// detection is suppressed until the player undoes the spill.
//
// Scales with region size on the same SMALL/LARGE_REGION_PIXELS axis as
// mass fraction and search radius. A flat ratio is too strict for tiny
// regions: a 200px pupil at 3× = 600px budget, which a single pen
// stroke can already swamp during normal play. The cap really only
// needs to catch accidental flood-fill spills (50K+ pixels of stray
// ink), so small regions can have a generous multiplier without
// losing the spill catch — at 20× a 200px pupil still trips on a
// 5K spill but tolerates 4K of legitimate scribble. The large end
// stays at 3× — a 50K beard with a 800K-canvas flood fill (~16× of
// the region) still trips loudly.
//
// Per-region `maxOverdrawRatio` in reference.json overrides the curve
// entirely for regions that legitimately want a different cap.
export const DEFAULT_SMALL_MAX_OVERDRAW_RATIO = 20.0;
export const DEFAULT_LARGE_MAX_OVERDRAW_RATIO = 3.0;

// Speedrun detection gate. The progress bar's *displayed* score animates
// upward toward the raw score at a tool-dependent rate, and a region
// commits when displayed reaches 1.0 — so the bar visibly fills to the
// top before the player advances rather than sitting full while a hidden
// timer counts down. Rates differ by player intent: after a fill the
// player has likely just blocked in a region and wants to advance, so
// the bar races; after a brush stroke they're more likely to keep
// refining, so the bar climbs slowly.
//
// DETECTION_FILL_MS / DETECTION_STROKE_MS are time-to-full from 0,
// linear. Snap-down on raw score drop is instant (no rate cap).
export const DETECTION_FILL_MS = 400;
export const DETECTION_STROKE_MS = 600;

// When a region commits, the speedrun panel holds the full bar for this
// long while a flash sweeps the panel and the title slides up out / the
// next label slides up in. Long enough to register as a completion beat,
// short enough not to gum up the speedrun pace. The commit chime
// (sound.js: playCommit) fires at the start of this window via
// MarcEngine's onCelebrationStart callback.
export const CELEBRATION_MS = 325;

// Count-in shown over the score panel during the pre-round window.
// Two sub-phases:
//   1. Waiting — speedrun entered + lineup picked, but the round
//      timer hasn't started (egg flying in, reference loading). The
//      panel is dimmed and the count-in slot shows "…". Derived in
//      ScorePanel from `roundSnapshot.startedAt == null` so it
//      covers the first paint cleanly (no race with the count-in
//      state machine, no "no labeled regions yet" flash).
//   2. Counting — once the egg has arrived, 3 → 2 → 1 → GO plays.
//      "3" / "2" / "1" each hold for COUNT_IN_BEAT_MS; "GO" gets
//      its own (shorter) duration so the round starts snappily —
//      the goal is "go play the game," not "linger on GO." On GO
//      end, markStarted fires (timer begins, input unlocks) and the
//      numeral fades out over COUNT_IN_EXIT_MS while the panel
//      content fades back to full opacity.
//
// `?debug` bypasses the count-in entirely so iteration stays fast.
export const COUNT_IN_BEATS = ["3", "2", "1", "GO"];
export const COUNT_IN_BEAT_MS = 500;
export const COUNT_IN_GO_MS = 350;
export const COUNT_IN_EXIT_MS = 200;

// Marc-completion showcase. After a marc's last region detects (and the
// per-region celebration window finishes), the easel photo cross-fades
// out as a marc overlay cross-fades in over the player's drawing at the
// same marcTransform position the OnionMarc would occupy. The overlay
// holds for a beat, then phase 3 hands off: advanceMarc fires (egg tween
// + easel src swap to the next marc), canvas overlay fades out, easel
// fades back in showing the new marc — all concurrent.
//
// No slide, no falling-into-frame. The earlier slide direction implied
// the photo was a discrete object jumping into a different container,
// which it isn't — both the easel and the canvas overlay live in their
// own coord spaces and share the marcTransform. Cross-fade reads as
// "the marc is the egg / the egg is the marc," which is the actual
// punchline.
//
// Timing breakdown (within SHOWCASE_MS):
//   0 → FADE_IN_MS                  — phase 1: canvas overlay opacity
//                                     0 → HOLD_OPACITY; easel opacity
//                                     1 → 0 (concurrent cross-fade).
//   FADE_IN_MS → FADE_IN+HOLD_MS    — phase 2: hold. Canvas at peak,
//                                     easel hidden.
//   FADE_IN+HOLD_MS → SHOWCASE_MS   — phase 3: advanceMarc fires at
//                                     the start of this window, so the
//                                     egg tween and the easel's src
//                                     swap (to the next marc) both
//                                     happen invisibly while the easel
//                                     is at opacity 0. Canvas overlay
//                                     fades out (HOLD_OPACITY → 0);
//                                     easel fades in (0 → 1, now showing
//                                     the new marc).
//
// HOLD_OPACITY is intentionally less than 1 so the player's drawing
// is still visible UNDER the photo during the hold — the punchline
// is the OVERLAP, not a replacement.
export const SHOWCASE_FADE_IN_MS = 600;
export const SHOWCASE_HOLD_MS = 1000;
export const SHOWCASE_FADE_OUT_MS = 500;
export const SHOWCASE_MS =
  SHOWCASE_FADE_IN_MS + SHOWCASE_HOLD_MS + SHOWCASE_FADE_OUT_MS;
export const SHOWCASE_HOLD_OPACITY = 0.85;

// Landing-page cycling preview. Per-marc beat:
//
//   1. egg moves into position (or initial settle on first marc)
//   2. drawing + photo fade in
//   3. hold
//   4. drawing + photo fade out
//   5. swap marcId; egg tweens to next marc's transform
//   6. (loops to step 2)
//
// The egg itself is never hidden — only the photo and the trace fade.
// Marc-to-marc differences in eggTransform are smoothed via a JS tween
// (see useTweenedEggTransform), with the canvas re-lighting per frame.
// Lighting snaps at marc swap; that snap happens mid-motion while
// drawing+photo are invisible, so it reads as part of the move rather
// than as a flash.
//
// LANDING_INITIAL_SETTLE_MS — first-paint delay before fading in the
//   first marc's drawing/photo. Gives the page a beat to settle.
// LANDING_HOLD_MS — how long each marc stays fully visible.
// LANDING_DRAWING_FADE_IN_MS / OUT_MS — drawing-trace fade durations.
// LANDING_PHOTO_FADE_IN_MS / OUT_MS — easel photo fade durations
//   (split from drawing so we can stagger later if we want).
// LANDING_EGG_MOVE_MS — duration of the egg's transform tween between
//   marcs.
export const LANDING_INITIAL_SETTLE_MS = 600;
export const LANDING_HOLD_MS = 4500;
export const LANDING_DRAWING_FADE_IN_MS = 800;
export const LANDING_DRAWING_FADE_OUT_MS = 600;
export const LANDING_PHOTO_FADE_IN_MS = 800;
export const LANDING_PHOTO_FADE_OUT_MS = 600;
export const LANDING_EGG_MOVE_MS = 800;

// Once a region has been committed, it stays "satisfied" as long as its
// raw score remains ≥ this fraction of the original threshold. If the
// player undoes / erases enough to drop below this, the region flips to
// "deficient" — the next-target picker re-queues it (after any still-
// uncommitted regions) so the marc can't be completed with a component
// missing. Tiny corrective erases stay above the threshold and don't
// un-advance you.
export const DETECTION_DEFICIENT_THRESHOLD = 0.5;
